import type { IAlert } from '../../alert-kinds/index.js';

/**
 * What wording needs of a client's translation: a key and its values in, the
 * words out. Both apps' i18next `t` is one; shared stays free of i18next.
 */
export type AlertTranslate = (key: string, options?: Record<string, unknown>) => string;

/** How an alert reads: what happened, and — when the record says more — a second line. */
export interface IAlertWording {
  text: string;
  detail?: string;
}

const LIST_LIMIT = 3;

/** "a, b, c +2" — a list cut to what a row can hold. */
function shortList(t: AlertTranslate, items: string[]): string {
  const shown = items.slice(0, LIST_LIMIT).join(', ');
  const more = items.length - LIST_LIMIT;
  return more > 0 ? t('notifications.alerts.andMore', { list: shown, count: more }) : shown;
}

const place = (...parts: (string | undefined)[]) => parts.filter(Boolean).join(' ');

/**
 * An alert in words, the same on the web and the phone (alerts spec, D4; the
 * keys are `notifications.alerts.*` in each app's catalogue). Every kind
 * reads from its own record; a record whose details could not be read reads
 * as its kind alone. D-ORCA's words, never the engine's.
 */
export function wordAlert(t: AlertTranslate, alert: IAlert): IAlertWording {
  const key = (k: string) => `notifications.alerts.${alert.kind}.${k}`;
  const fallback = { text: t(key('title')) };
  switch (alert.kind) {
    case 'safety.distress':
    case 'safety.restrictedArea': {
      const d = alert.details;
      if (!d) return fallback;
      // Only the parts the record has: a tag, a worker's name, a place.
      const tag = 'tagNick' in d ? d.tagNick : d.tagId;
      const who = [t('notifications.alerts.tag', { tag }), d.workerName].filter(Boolean).join(' ');
      const where = place(d.floor, d.area);
      return { text: t(key('title')), detail: where ? t(key('detail'), { who, place: where }) : who };
    }
    case 'reports.dailyOverview':
      return fallback;
    case 'reports.tagHealth': {
      const d = alert.details;
      if (!d) return fallback;
      const lines = [
        d.lowBatteryTags.length > 0 && t(key('lowBattery'), { list: shortList(t, d.lowBatteryTags) }),
        d.unseenTags.length > 0 && t(key('unseen'), { list: shortList(t, d.unseenTags) }),
      ].filter((line): line is string => typeof line === 'string');
      return { text: t(key('text'), { count: d.lowBatteryTags.length + d.unseenTags.length }), detail: lines.join(' · ') };
    }
    case 'activities.planUpdated': {
      const d = alert.details;
      return d ? { text: t(key('text'), { name: d.byName }) } : fallback;
    }
    case 'activities.statusUpdated': {
      const d = alert.details;
      if (!d) return fallback;
      const status = t(`notifications.alerts.areaStatus.${d.status}`, { defaultValue: d.status });
      return {
        text: t(key('text'), { name: d.byName, item: d.itemName }),
        detail: t(key('detail'), { status, areas: shortList(t, d.areas) }),
      };
    }
    case 'issues.createdSummary': {
      const d = alert.details;
      if (!d) return fallback;
      return {
        text: t(key(d.weekly ? 'textWeekly' : 'textDaily'), { count: d.descriptions.length }),
        detail: shortList(t, d.descriptions),
      };
    }
    case 'issues.commented': {
      const d = alert.details;
      if (!d) return fallback;
      return {
        text: t(key('text'), { name: d.byName, issue: d.description }),
        detail: d.comment ?? (d.attachment ? t(key('attachment')) : undefined),
      };
    }
    case 'issues.assigned': {
      const d = alert.details;
      return d ? { text: t(key('title')), detail: d.description } : fallback;
    }
    case 'issues.statusChanged': {
      const d = alert.details;
      if (!d) return fallback;
      const status = t(`notifications.alerts.issueStatus.${d.status}`, { defaultValue: d.status });
      return { text: t(key('text'), { issue: d.description, status }) };
    }
  }
}
